import { HistoryItem } from '@wartek-id/breadcrumb'

import { ENDPOINT } from '@/utils/fetcher/fetcher.helpers'
import { fetchOnServer } from '@/utils/fetcher/fetcher.server'
import { xssSafeString } from '@/utils/string'
import { LocationLevel, LocationLevelEnum } from '@/utils/tracker/shared'
import {
  DataIndukVariant,
  DistrictAncestorsResponse,
  NestedSatpenDistrict,
  PtkStats,
  PtkStatsByParentWilayahResponseData,
  SatpenStats,
  SatpenStatsByParentWilayahResponseData,
  SatpenTableItemStats,
  YayasanStatsByParentWilayahResponseData,
} from '@/apis/types'

import {
  DATA_INDUK_ROUTE,
  DEFAULT_METADATA,
  DEFAULT_METATAG_OPENGRAPH,
  DEFAULT_METATAG_TWITTER,
} from '@/configs/constants'

import { VARIANT } from './constant'
import {
  DEFAULT_LOGO,
  KODE_WILAYAH_LUAR_NEGERI,
  KODE_WILAYAH_NASIONAL_FOR_TRACKER,
  PROVINCES_LOGO_BY_CODE,
} from './provinces'

export const statsToTableArray = (
  stats: SatpenStats | PtkStats,
  variant: DataIndukVariant = VARIANT.SATPEN
): SatpenTableItemStats[] => {
  if (!stats) {
    return []
  }

  if (variant === VARIANT.YAYASAN) {
    return [
      {
        text: 'Yayasan',
        value: stats?.total || 0,
        id: 'yayasan',
      },
    ]
  }

  const satpenStatsResult = [
    {
      text: 'KB/Sederajat',
      value: stats?.kbSederajat || 0,
      id: 'kb',
    },
    {
      text: 'TK/Sederajat',
      value: stats?.tkSederajat || 0,
      id: 'tk',
    },
    {
      text: 'TPA',
      value: stats?.tpa || 0,
      id: 'tpa',
    },
    {
      text: 'SPS',
      value: stats?.sps || 0,
      id: 'sps',
    },
    {
      text: 'SD/Sederajat',
      value: stats?.sdSederajat || 0,
      id: 'sd',
    },
    {
      text: 'SMP/Sederajat',
      value: stats?.smpSederajat || 0,
      id: 'smp',
    },
    {
      text: 'SMA/Sederajat',
      value: stats?.smaSederajat || 0,
      id: 'sma',
    },
    {
      text: 'SMK/Sederajat',
      value: stats?.smkSederajat || 0,
      id: 'smk',
    },
    {
      text: 'SLB/Sederajat',
      value: stats?.slb || 0,
      id: 'slb',
    },
    {
      text: 'DIKMAS Lainnya',
      value: stats?.dikmas || 0,
      id: 'dikmas',
    },
    {
      text: 'Total',
      value: stats?.total || 0,
      id: 'total',
    },
  ]

  if (variant === VARIANT.PTK) {
    return [
      ...satpenStatsResult,
      {
        text: 'Kepala Sekolah',
        // @ts-ignore
        value: stats?.kepalaSekolah || 0,
        id: 'kepsek',
        footer: true,
      },
    ]
  }

  return satpenStatsResult
}

export const ancestorToBreadcrumb = ({
  level,
  withRoot,
  pathPrefix = DATA_INDUK_ROUTE,
  nestedDistrict,
}: {
  level: number
  withRoot: boolean
  pathPrefix?: string
  nestedDistrict?: NestedSatpenDistrict | null
}): HistoryItem[] => {
  if (!nestedDistrict) {
    return []
  }

  const ALL_ITEM = {
    href: pathPrefix,
    id: 'lvl-0',
    text: 'Semua',
  } as HistoryItem

  const PROVINCE_ITEM = {
    href: `${pathPrefix}/${nestedDistrict.child?.kodeWilayah}`,
    id: 'lvl-1',
    text:
      nestedDistrict.child?.namaWilayahDagri ||
      nestedDistrict.child?.namaWilayah ||
      '',
  } as HistoryItem

  if (level === 1) {
    if (withRoot) {
      return [ALL_ITEM, PROVINCE_ITEM]
    }
    return [PROVINCE_ITEM]
  }

  if (level === 2) {
    const KAB_KOT_ITEM = {
      href: `${pathPrefix}/${nestedDistrict.child?.kodeWilayah}/${nestedDistrict.child?.child?.kodeWilayah}`,
      id: 'lvl-2',
      text:
        nestedDistrict.child?.child?.namaWilayahDagri ||
        nestedDistrict.child?.child?.namaWilayah ||
        '',
    } as HistoryItem

    if (withRoot) {
      return [ALL_ITEM, PROVINCE_ITEM, KAB_KOT_ITEM]
    }

    return [PROVINCE_ITEM, KAB_KOT_ITEM]
  }

  if (level === 3) {
    const KAB_KOT_ITEM = {
      href: `${pathPrefix}/${nestedDistrict.child?.kodeWilayah}/${nestedDistrict.child?.child?.kodeWilayah}`,
      id: 'lvl-2',
      text:
        nestedDistrict.child?.child?.namaWilayahDagri ||
        nestedDistrict.child?.child?.namaWilayah ||
        '',
    } as HistoryItem

    const KEC_ITEM = {
      href: `${pathPrefix}/${nestedDistrict.child?.kodeWilayah}/${nestedDistrict.child?.child?.kodeWilayah}/${nestedDistrict.child?.child?.child?.kodeWilayah}`,
      id: 'lvl-3',
      text:
        nestedDistrict.child?.child?.child?.namaWilayah ||
        nestedDistrict.child?.child?.child?.namaWilayahDagri ||
        '',
    } as HistoryItem

    if (withRoot) {
      return [ALL_ITEM, PROVINCE_ITEM, KAB_KOT_ITEM, KEC_ITEM]
    }

    return [PROVINCE_ITEM, KAB_KOT_ITEM, KEC_ITEM]
  }

  if (withRoot) {
    return [ALL_ITEM]
  }

  return []
}

export const getLatestWilayahName = ({
  data,
}: {
  data?: DistrictAncestorsResponse | undefined
}) => {
  if (data && data?.district) {
    // Level 3
    if (data?.district?.child?.child?.child) {
      return (
        // namaWilayah contains KEC.
        data?.district?.child?.child?.child?.namaWilayah ||
        data?.district?.child?.child?.child?.namaWilayahDagri
      )
    }

    // Level 2
    else if (data?.district?.child?.child) {
      return (
        data?.district?.child?.child?.namaWilayahDagri ||
        data?.district?.child?.child?.namaWilayah
      )
    }

    // Level 1
    else if (data?.district?.child) {
      return (
        data?.district?.child?.namaWilayahDagri ||
        data?.district?.child?.namaWilayah
      )
    }

    // Level 0
    return data?.district?.namaWilayahDagri || data?.district?.namaWilayah
  }

  return ''
}

export const getWilayahByLevel = ({
  data,
  level = LocationLevelEnum.NASIONAL,
}: {
  data?: DistrictAncestorsResponse | undefined
  level: LocationLevel
}) => {
  if (data && data?.district) {
    // Level Kecamatan
    if (
      level === LocationLevelEnum.KECAMATAN &&
      data?.district?.child?.child?.child
    ) {
      return (
        // namaWilayah contains KEC.
        data?.district?.child?.child?.child?.namaWilayah ||
        data?.district?.child?.child?.child?.namaWilayahDagri
      )
    }

    // Level Kabupaten
    else if (
      level === LocationLevelEnum.KABUPATEN &&
      data?.district?.child?.child
    ) {
      return (
        data?.district?.child?.child?.namaWilayahDagri ||
        data?.district?.child?.child?.namaWilayah
      )
    }

    // Level Province
    else if (level === LocationLevelEnum.PROVINSI && data?.district?.child) {
      return (
        data?.district?.child?.namaWilayahDagri ||
        data?.district?.child?.namaWilayah
      )
    }

    // Level Nasional
    return data?.district?.namaWilayahDagri || data?.district?.namaWilayah
  }

  return level
}

export const getLogoProvinceLink = ({
  data,
  withDefault = false,
}: {
  data?: DistrictAncestorsResponse | undefined
  withDefault?: boolean
}) => {
  if (data && data?.district) {
    const isLN = data?.district?.child?.kodeWilayah === KODE_WILAYAH_LUAR_NEGERI

    if (isLN && data?.district?.child?.child) {
      const result =
        PROVINCES_LOGO_BY_CODE[
          data?.district?.child?.child?.kodeWilayah as string
        ]

      if (withDefault && !result) {
        return DEFAULT_LOGO
      }

      return result
    } else if (data?.district?.child) {
      const result =
        PROVINCES_LOGO_BY_CODE[data?.district?.child?.kodeWilayah as string]

      if (withDefault && !result) {
        return DEFAULT_LOGO
      }

      return result
    }
  }

  if (withDefault) {
    return DEFAULT_LOGO
  } else {
    return ''
  }
}

export const DEFAULT_LIMIT = 20

export const injectImageProvince = (
  data:
    | SatpenStatsByParentWilayahResponseData[]
    | YayasanStatsByParentWilayahResponseData[]
    | PtkStatsByParentWilayahResponseData[]
) => {
  return (data || []).map((item) => {
    return {
      ...item,
      // @ts-ignore
      image: PROVINCES_LOGO_BY_CODE[item.district.kodeWilayah] || DEFAULT_LOGO,
    }
  })
}

export const sumTotalStatsYayasan = (
  data: YayasanStatsByParentWilayahResponseData[]
): number => {
  return (data || []).reduce((accumulator, currentValue) => {
    return accumulator + (currentValue?.yayasanPendidikanStatistics?.total || 0)
  }, 0)
}

export const getValidSearchTrackerParams = ({
  keyword = '',
  locationId,
  locationLevel,
}) => {
  const cleanKeyword = xssSafeString(keyword)
  const cleanLocationId = xssSafeString(locationId)
  const validLocationId =
    cleanLocationId.length === 6
      ? cleanLocationId
      : KODE_WILAYAH_NASIONAL_FOR_TRACKER

  let cleanLocationLevel = xssSafeString(locationLevel)
  if (
    cleanLocationLevel !== 'Nasional' ||
    cleanLocationLevel !== 'Provinsi' ||
    cleanLocationLevel !== 'Kabupaten' ||
    cleanLocationLevel !== 'Kecamatan'
  ) {
    cleanLocationLevel = 'Nasional'
  }

  return {
    keyword: cleanKeyword,
    locationId: validLocationId,
    locationLevel: cleanLocationLevel,
  }
}

export const getPromiseWilayahAncestorsSatpen = ({
  kodeWilayah,
  xForwardedFor,
}: {
  kodeWilayah: string
  xForwardedFor: string
}) => {
  const CACHE_TAG = `satpen-breadcrumbs-${kodeWilayah}`
  const promise = fetchOnServer<DistrictAncestorsResponse>(
    `${ENDPOINT.DATA_INDUK_SATPEN}/districts/${kodeWilayah}/ancestors`,
    {
      'X-Forwarded-For': xForwardedFor || '',
    },
    {
      // revalidate at most every 24 hours
      next: {
        revalidate: 3600 * 24,
        tags: [CACHE_TAG],
      },
      ssr: true,
    }
  )

  return {
    promise: promise,
    cacheTag: CACHE_TAG,
  }
}

export const getDataBreadcrumbSatpen = async ({
  kodeWilayah,
  xForwardedFor,
}: {
  kodeWilayah: string
  xForwardedFor: string
}) => {
  const { promise: promiseBreadcrumb } = getPromiseWilayahAncestorsSatpen({
    kodeWilayah: kodeWilayah,
    xForwardedFor: xForwardedFor,
  })

  const promises = [promiseBreadcrumb]
  const [dataBreadcrumb] = await Promise.all(promises)

  return dataBreadcrumb as DistrictAncestorsResponse
}

export const getDynamicMetadataDataInduk = async ({
  kodeWilayah,
  metaUrl,
  titlePrefix,
  xForwardedFor,
}: {
  kodeWilayah: string
  metaUrl: string
  titlePrefix: string
  xForwardedFor: string
}) => {
  const dataBreadcrumb = await getDataBreadcrumbSatpen({
    kodeWilayah,
    xForwardedFor,
  })

  const title = `${titlePrefix} ${getLatestWilayahName({ data: dataBreadcrumb })} | ${DEFAULT_METADATA.title}`

  const description = `Laman ${titlePrefix} Resmi untuk Wilayah ${getLatestWilayahName({ data: dataBreadcrumb })}`

  return {
    alternates: {
      canonical: metaUrl,
    },
    title,
    description,
    openGraph: {
      ...DEFAULT_METATAG_OPENGRAPH,
      title,
      description,
      url: metaUrl,
    },
    twitter: {
      ...DEFAULT_METATAG_TWITTER,
      title,
      description,
    },
  }
}
